import { toToneNum } from './pinyin';
import type { AzurePronunciationResult } from './azureClient';

export type Outcome = 'critical' | 'hit' | 'glance' | 'miss' | 'fumble';

export interface ScoredAttempt {
  score: number;            // 0–100
  outcome: Outcome;
  damageMultiplier: number; // applies to baseDamage
  selfDamagePct: number;    // % of maxHp the hero loses
  heardHanzi: string;
  heardPinyinNum: string;
  expectedPinyinNum: string;
  exactHanziMatch: boolean;
}

// Levenshtein distance (iterative, single-row).
function lev(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

// Score formula:
//   exact hanzi match              → 100 (the recognizer is very confident the kid said the target)
//   else pinyin similarity 0–100   → 1 - lev(target, heard)/maxLen
//
// Phase 0 / Web Speech caveat: the recognizer auto-corrects close pronunciations
// to the most-likely hanzi, so this scoring detects wrong-WORD errors well but
// not wrong-TONE-only errors. The plan accepts that and ships native-audio
// replay as the pedagogical fix.
export function scoreUtterance(
  expectedHanzi: string,
  expectedPinyinNum: string,   // pre-computed; pass card.pinyinNum
  heardHanzi: string,
): { score: number; heardPinyinNum: string; exactHanziMatch: boolean } {
  const heardClean = (heardHanzi || '').trim();
  if (!heardClean) return { score: 0, heardPinyinNum: '', exactHanziMatch: false };

  if (heardClean === expectedHanzi) {
    return {
      score: 100,
      heardPinyinNum: expectedPinyinNum,
      exactHanziMatch: true,
    };
  }

  const heardPy = toToneNum(heardClean);
  const targetPy = expectedPinyinNum || toToneNum(expectedHanzi);
  if (!targetPy || !heardPy) {
    return { score: 0, heardPinyinNum: heardPy, exactHanziMatch: false };
  }

  const distance = lev(targetPy, heardPy);
  const denom = Math.max(targetPy.length, heardPy.length);
  const similarity = 1 - distance / denom;
  // Compress the bottom so a near-miss in pinyin doesn't read as 50/100.
  // Squaring biases toward exactness without nuking partial credit.
  const score = Math.round(100 * similarity * similarity);
  return { score, heardPinyinNum: heardPy, exactHanziMatch: false };
}

// Map score → combat outcome.
export function outcomeFor(score: number, scoreFloor: number): Outcome {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'hit';
  if (score >= 50) return 'glance';
  if (score >= Math.max(30, scoreFloor)) return 'miss';
  return 'fumble';
}

const MULTS: Record<Outcome, number> = {
  critical: 1.5,
  hit: 1.0,
  glance: 0.6,
  miss: 0.0,
  fumble: 0.0,
};

const SELF_DAMAGE: Record<Outcome, number> = {
  critical: 0,
  hit: 0,
  glance: 0,
  // A miss costs a small HP nibble (~1 HP at maxHp=5). Without this, a player
  // whose mic isn't working can't ever game-over and would be trapped in the
  // zone forever. With it, ~5 silent attempts in a row sends them back to the
  // academy where they can fix mic settings.
  miss: 0.15,
  fumble: 0.25,
};

export function judgeAttempt(
  expectedHanzi: string,
  expectedPinyinNum: string,
  heardHanzi: string,
  scoreFloor: number = 30,
): ScoredAttempt {
  const { score, heardPinyinNum, exactHanziMatch } = scoreUtterance(expectedHanzi, expectedPinyinNum, heardHanzi);
  const outcome = outcomeFor(score, scoreFloor);
  return {
    score,
    outcome,
    damageMultiplier: MULTS[outcome],
    selfDamagePct: SELF_DAMAGE[outcome],
    heardHanzi,
    heardPinyinNum,
    expectedPinyinNum,
    exactHanziMatch,
  };
}

// Azure-flavored judgement. Uses Azure's per-syllable accuracy + tone data
// rather than transcript Levenshtein. Tone-only errors cap at "glance" — that's
// the whole point of Azure over Web Speech.
export interface AzureScoredAttempt extends ScoredAttempt {
  tonesCorrect: boolean;
  syllableScores: number[];
}

export function judgeAzureAttempt(
  expectedHanzi: string,
  expectedPinyinNum: string,
  azure: AzurePronunciationResult,
  scoreFloor: number = 30,
): AzureScoredAttempt {
  // Use Azure's overall accuracy as the headline 0-100 score. It already
  // factors in per-syllable and per-phoneme correctness.
  const score = Math.round(azure.accuracy);

  // Detect tone-only errors: all syllables present but at least one has
  // accuracy below the tone threshold AND the transcript matched the target.
  const transcriptMatches = (azure.transcript || '').trim() === expectedHanzi;
  const anyToneIssue = azure.syllables.length > 0 && azure.syllables.some((s) => !s.tonePassed);
  const tonesCorrect = !anyToneIssue;

  let outcome = outcomeFor(score, scoreFloor);
  // Tone-only error rule: if the kid said the right word but blew a tone,
  // cap damage at "glance" (60% damage) regardless of how generous Azure was.
  // This is the rule the pedagogy memo called the single most important lever
  // in the entire game.
  if (transcriptMatches && !tonesCorrect && (outcome === 'critical' || outcome === 'hit')) {
    outcome = 'glance';
  }

  return {
    score,
    outcome,
    damageMultiplier: MULTS[outcome],
    selfDamagePct: SELF_DAMAGE[outcome],
    heardHanzi: azure.transcript,
    heardPinyinNum: '',
    expectedPinyinNum,
    exactHanziMatch: transcriptMatches,
    tonesCorrect,
    syllableScores: azure.syllables.map((s) => Math.round(s.accuracy)),
  };
}
